import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useClassNames } from "@/hooks/useClassNames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { MenuItem } from "@/types/domain";
import { popupCenter } from "@/utils/popup";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { MenuIcon } from "./MenuIcon";
import { FavouritesSubMenu } from "./FavouritesSubMenu";
import { PipelinesSubMenu } from "./PipelinesSubMenu";
import { MiniItemFlyout } from "./MiniItemFlyout";

// ─── Single menu item ────────────────────────────────────────────

export type MenuItemRowProps = {
  item: MenuItem;
  isMini: boolean;
  depth?: number;
  openItemId?: string | null;
  onToggleItem?: (id: string) => void;
}

export const MenuItemRow = ({ item, isMini, depth = 0, openItemId, onToggleItem }: MenuItemRowProps) => {
  const [localExpanded, setLocalExpanded] = useState(item.isSelected);
  const expanded = depth === 0 && openItemId !== undefined ? openItemId === item.id : localExpanded;
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const showFlyout = isMini && depth === 0;

  // Scroll selected sub-item into view on initial render
  useEffect(() => {
    if (item.isSelected && depth > 0 && itemRef.current) {
      itemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, []);

  if (!item.isVisible) return null;

  const isExpandable = item.type === "dropdown" || item.type === "favourites" || item.type === "pipelines";
  const isOpen = isExpandable && expanded && !isMini;
  const hasSelectedChild = isExpandable && item.children?.some((c) => c.isSelected) === true;

  const selectedClass = isOpen
    ? "text-[var(--layout-side-nav-text)]"
    : item.isSelected
      ? "text-white bg-[var(--layout-side-nav-active-bg)] rounded-l-2xl"
      : "text-[var(--layout-side-nav-text)] hover:bg-white hover:text-black rounded-l-2xl";

  const iconClass = useClassNames("shrink-0", !isOpen && !item.isSelected && "group-hover:text-[var(--layout-side-nav-active-bg)]");

  const icon = item.icon ? (
    <MenuIcon name={item.icon} className={iconClass} />
  ) : depth > 0 ? (
    <span className="shrink-0 inline-block" style={{ width: 8 }} aria-hidden="true" />
  ) : null;

  const labelEl = !isMini && (
    <span className={useClassNames("truncate flex-1", hasSelectedChild && "font-bold")}>{item.label}</span>
  );

  const chevronEl = !isMini && isExpandable && (
    expanded
      ? <FontAwesomeIcon icon={faChevronDown} className="shrink-0" size="xs" />
      : <FontAwesomeIcon icon={faChevronRight} className="shrink-0" size="xs" />
  );

  const rowInner = (
    <div
      className={useClassNames("group flex items-center gap-2 py-2 cursor-pointer transition-colors rounded-l-2xl", selectedClass)}
      style={{ paddingLeft: `${10 + depth * 10}px`, paddingRight: "10px" }}
    >
      {icon}
      {labelEl}
      {chevronEl}
    </div>
  );

  const openFlyout = () => {
    setFlyoutOpen((v) => !v);
  }

  const closeFlyout = () => {
    setFlyoutOpen(false);
  }

  const handleExpandClick = () => {
    if (depth === 0 && onToggleItem) {
      onToggleItem(item.id);
    } else {
      setLocalExpanded((v) => !v);
    }
  }

  const handlePopupClick = (e: MouseEvent) => {
    e.preventDefault();
    popupCenter(
      item.url ?? "#",
      item.popupWindowName ?? "_blank",
      item.popupWidth ?? 1024,
      item.popupHeight ?? 768,
    );
  }

  let rowEl: ReactNode;

  if (isMini) {
    if (showFlyout && isExpandable) {
      rowEl = (
        <div>
          <div role="button" tabIndex={0} onClick={openFlyout}>{rowInner}</div>
          {flyoutOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={closeFlyout} aria-hidden="true" />
              <MiniItemFlyout item={item} />
            </>
          )}
        </div>
      );
    } else {
      rowEl = (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              {item.type === "link" ? (
                <a href={item.url ?? "#"} className="block">{rowInner}</a>
              ) : item.type === "popup" ? (
                <div role="button" tabIndex={0} onClick={handlePopupClick}>{rowInner}</div>
              ) : (
                <div role="button" tabIndex={0} onClick={handleExpandClick}>{rowInner}</div>
              )}
            </TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  } else if (item.type === "link") {
    rowEl = <a href={item.url ?? "#"} className="block">{rowInner}</a>;
  } else if (item.type === "popup") {
    rowEl = <div role="button" tabIndex={0} onClick={handlePopupClick}>{rowInner}</div>;
  } else {
    rowEl = (
      <div
        role="button"
        tabIndex={0}
        onClick={handleExpandClick}
        onKeyDown={(e) => e.key === "Enter" && handleExpandClick()}
      >
        {rowInner}
      </div>
    );
  }

  return (
    <li
      ref={itemRef}
      className="menu-item mb-0"
      style={isOpen ? { backgroundColor: "var(--layout-side-nav-sub-bg)", borderTopLeftRadius: "16px" } : undefined}
    >
      {rowEl}
      {isOpen && (
        <ul className="pb-1 space-y-0.5">
          {item.type === "favourites" && <FavouritesSubMenu />}
          {item.type === "pipelines" && <PipelinesSubMenu />}
          {item.type === "dropdown" &&
            item.children?.map((child) => (
              <MenuItemRow key={child.id} item={child} isMini={isMini} depth={depth + 1} openItemId={undefined} />
            ))}
        </ul>
      )}
    </li>
  );
}
